import { useEffect, useState } from 'react';
import { useSavedListingContext, useUserContext } from 'contexts';
import type { ListingCardData } from 'components/listing-card';
import type { SavedListingFull } from 'contexts/saved-listing';

/** Narrows a saved-listing item down to its embedded listing, dropping ones without one. */
function hasListing(saved: SavedListingFull): saved is SavedListingFull & { listing: NonNullable<SavedListingFull['listing']> } {
  return Boolean(saved.listing);
}

/** Favourites shown per page. */
const FAVOURITES_PAGE_SIZE = 36;

const NO_LISTINGS: readonly ListingCardData[] = [];

type LoadedPage = {
  pageNumber: number;
  listings: readonly ListingCardData[];
};

export type UseCollectionsFavouritesResult = {
  /** Listing-card data for the current page only. */
  listings: readonly ListingCardData[];
  isLoading: boolean;
  /** True when the page's fetch failed, as opposed to returning nothing. */
  hasError: boolean;
  /** Still-listed favourites in total, or `undefined` until the first page lands. */
  count: number | undefined;
  pageNumber: number;
  totalPages: number;
  setPageNumber: (pageNumber: number) => void;
};

/**
 * The signed-in user's still-listed favourites, one page at a time. Sold and
 * expired ones (the endpoint's `inactive` half) are deliberately not fetched
 * for now — only `available` is asked for, so a page is never part-active
 * part-sold and nothing has to be merged across the two.
 *
 * Page count and the tab's own count come from the response's own
 * `meta.totalCount`, so no separate counting request is needed.
 *
 * Cards are built from the listing embedded in each saved-listing row. That
 * embed carries everything a card needs, including a `standard_status` the
 * client-app parser reads correctly, so there's no second hydration request —
 * and no id that a second source could fail to resolve, silently dropping the
 * row.
 *
 * `isActive` gates the fetch so the pane only loads the tab being looked at.
 * The grid renders nothing until the page it's showing has fully arrived.
 */
export function useCollectionsFavourites(isActive: boolean): UseCollectionsFavouritesResult {
  const { isAuthenticated } = useUserContext();
  const { getSavedListings, mergeSavedListings } = useSavedListingContext();
  const [pageNumber, setPageNumber] = useState(1);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [hasError, setHasError] = useState(false);
  const [loaded, setLoaded] = useState<LoadedPage | null>(null);

  const totalPages = count === undefined ? 1 : Math.max(1, Math.ceil(count / FAVOURITES_PAGE_SIZE));
  // Unfavouriting can shrink the set below the page being viewed. Clamping
  // here rather than correcting `pageNumber` keeps this derived: the page
  // fetched and the page shown are both the clamped one, so the user is never
  // left on an empty page with no pager to leave it.
  const effectivePage = Math.min(pageNumber, totalPages);

  useEffect(() => {
    if (!isActive || !isAuthenticated) {
      return undefined;
    }

    let cancelled = false;

    // Only a retry of a page that failed discards what's on screen, so the
    // error can't linger through its own refetch. A page that loaded fine
    // stays rendered while it refreshes (e.g. on returning to the tab), and a
    // move to a different page is already covered by the key mismatch below.
    if (hasError) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoaded(null);
      setHasError(false);
    }

    getSavedListings({
      page: { size: FAVOURITES_PAGE_SIZE, number: effectivePage },
      sort: '-date',
      filter: { status: 'available' },
    }).then(result => {
      if (cancelled) return;

      if (result.error) {
        // The failed page stays the current one so its error is what shows;
        // `count` is left alone both to avoid publishing a 0 the request never
        // reported and to keep the pager rendered, which is how the user
        // leaves the error.
        setHasError(true);
        setLoaded({ pageNumber: effectivePage, listings: NO_LISTINGS });
        return;
      }

      // This page can reach past what the app-wide context's own initial fetch
      // covers, so fold it in — otherwise a listing paged to here wouldn't show
      // as favourited anywhere else on the site.
      mergeSavedListings(result.savedListings);
      setHasError(false);
      setCount(result.meta.totalCount ?? 0);
      setLoaded({ pageNumber: effectivePage, listings: result.savedListings.filter(hasListing).map(saved => saved.listing) });
    }).catch(() => {
      if (cancelled) return;
      setHasError(true);
      setLoaded({ pageNumber: effectivePage, listings: NO_LISTINGS });
    });

    return () => {
      cancelled = true;
    };
  // The saved-listing context recreates `getSavedListings`/`mergeSavedListings`
  // every render, so depending on them would refetch in a loop. `hasError` is
  // read but deliberately omitted too: it should only be consulted when a new
  // request starts, not trigger one of its own.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isAuthenticated, effectivePage]);

  const currentPage = loaded !== null && loaded.pageNumber === effectivePage ? loaded.listings : null;

  return {
    listings: currentPage ?? NO_LISTINGS,
    isLoading: isActive && isAuthenticated && currentPage === null,
    hasError,
    count,
    pageNumber: effectivePage,
    totalPages,
    setPageNumber,
  };
}
